
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, AlertTriangle, TrendingUp, Shield, DollarSign, FileText } from "lucide-react";
import Navigation from "@/components/Navigation";

const AboutLandContracts = () => {
  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6 text-green-600" />,
      title: "Higher Yields",
      description: "Land contracts typically offer higher returns than traditional investments, often ranging from 8-12% annually."
    },
    {
      icon: <Shield className="h-6 w-6 text-green-600" />,
      title: "Asset-Backed Security",
      description: "Your investment is secured by real estate, providing tangible collateral that can be recovered if needed."
    },
    {
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      title: "Regular Cash Flow",
      description: "Receive consistent monthly payments from buyers, providing predictable income streams."
    },
    {
      icon: <FileText className="h-6 w-6 text-green-600" />,
      title: "Legal Protections",
      description: "Land contracts provide strong legal remedies including foreclosure rights and property retention options."
    }
  ];

  const risks = [
    {
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      title: "Buyer Default Risk",
      description: "If the buyer stops making payments, you may need to pursue foreclosure proceedings to recover the property."
    },
    {
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      title: "Property Management",
      description: "Depending on the contract terms, you may be responsible for property taxes, insurance, or maintenance issues."
    },
    {
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      title: "Market Risk",
      description: "Property values can fluctuate, potentially affecting the underlying collateral value of your investment."
    },
    {
      icon: <AlertTriangle className="h-6 w-6 text-red-600" />,
      title: "Property Condition Risk",
      description: "The property's condition may deteriorate over time if the buyer fails to maintain it properly, potentially reducing its value and your collateral security."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Navigation />
      
      <div className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              About Investing in Land Contracts
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn about land contract investments, their structure, benefits, and important considerations for investors.
            </p>
          </div>

          {/* What is a Land Contract */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">What is a Land Contract?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-gray-700">
                <p>
                  A land contract, also known as a contract for deed or installment sale agreement, is a financing arrangement 
                  where the seller acts as the bank. Instead of the buyer obtaining a traditional mortgage, they make payments 
                  directly to the seller over an agreed period.
                </p>
                <p>
                  <strong>How it works:</strong> The buyer moves into the property and makes monthly payments to the seller, 
                  but the seller retains legal title to the property until the contract is fully paid off. Once all payments 
                  are complete, ownership transfers to the buyer.
                </p>
                <p>
                  <strong>For investors:</strong> When you invest in a land contract, you're essentially purchasing the right 
                  to receive those monthly payments from the buyer. You become the new "seller" in the arrangement and receive 
                  the ongoing payment stream plus any balloon payment at the end of the term.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Benefits Section */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Benefits of Land Contract Investing
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      {benefit.icon}
                      <CardTitle className="text-lg">{benefit.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">
                      {benefit.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Risks and Considerations */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Risks and Considerations
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {risks.map((risk, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-red-200">
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      {risk.icon}
                      <CardTitle className="text-lg">{risk.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">
                      {risk.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Key Terms Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Key Terms to Understand</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Investment-to-Value (ITV)</h4>
                  <p className="text-gray-600 mb-4">
                    The ratio of your investment amount to the property's current market value. Lower ITV ratios provide better security.
                  </p>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">Balloon Payment</h4>
                  <p className="text-gray-600 mb-4">
                    A large lump sum payment due at the end of the contract term, often representing the remaining principal balance.
                  </p>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">Yield</h4>
                  <p className="text-gray-600 mb-4">
                    The annual return on your investment, calculated as a percentage of the total amount invested. This includes both monthly payments and any balloon payment.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Payment History</h4>
                  <p className="text-gray-600 mb-4">
                    The buyer's track record of making timely payments, which indicates the reliability of your income stream.
                  </p>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">Foreclosure Rights</h4>
                  <p className="text-gray-600 mb-4">
                    Your legal ability to reclaim the property if the buyer defaults on payments, providing investor protection.
                  </p>
                  
                  <h4 className="font-semibold text-gray-900 mb-2">Property Type</h4>
                  <p className="text-gray-600">
                    The classification of the real estate securing your investment, such as single-family homes, mobile homes, or vacant land, which affects risk and marketability.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Due Diligence Section */}
          <Card className="bg-gradient-to-r from-blue-50 to-yellow-50">
            <CardHeader>
              <CardTitle className="text-2xl">Due Diligence Checklist</CardTitle>
              <CardDescription>
                Important factors to evaluate before investing in any land contract opportunity
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Property Analysis
                  </h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Current market value assessment</li>
                    <li>• Property condition and inspection reports</li>
                    <li>• Neighborhood trends and comparables</li>
                    <li>• Tax and insurance status</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Buyer Evaluation
                  </h4>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Credit score and financial history</li>
                    <li>• Payment track record on this contract</li>
                    <li>• Reason for selling (original seller motivation)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutLandContracts;
